// ============================================================
// LEVEL 4 STRESS TEST — replaces the old "type a question, confirm
// a dialog that shows nothing" manual approval step.
//
// Haiku generates 3 questions grounded in the document's own
// extracted text (simple / moderate / adversarial), then each
// question runs through the REAL production RAG path
// (runRAGQuery — same function every homeowner query uses).
// The admin reads the real answers + sources and decides —
// this module never computes a pass/fail verdict itself.
// ============================================================

import { runRAGQuery } from './ragEngine';
import { documentsService } from '../services/documentsService';

const READINESS_POLL_INTERVAL_MS = 3000;
const READINESS_TIMEOUT_MS = 60000;

// Waits for chunking + embedding to actually finish before testing.
// Without this, a stress test could run against zero or partially-embedded
// chunks and produce a false "this document doesn't work" result that has
// nothing to do with the document itself — just a race with the
// fire-and-forget ingestion pipeline.
export async function waitForIngestionReady(documentId, onProgress) {
  const startedAt = Date.now();

  while (Date.now() - startedAt < READINESS_TIMEOUT_MS) {
    const status = await documentsService.getIngestionReadiness(documentId);
    if (onProgress) onProgress(status);
    if (status.ready) return status;
    if (status.error) return status;
    await new Promise(resolve => setTimeout(resolve, READINESS_POLL_INTERVAL_MS));
  }

  return {
    ready: false,
    error: 'Timed out waiting for chunking/embedding to finish. The document may still be processing — try again in a minute, or check the document for an indexing error.',
    chunkCount: 0,
    embeddedCount: 0,
  };
}

export async function generateStressTestQuestions(supabase, documentId, hoaId) {
  const { data, error } = await supabase.functions.invoke('generate-test-questions', {
    body: { document_id: documentId, hoa_id: hoaId },
  });

  if (error) throw new Error(`Question generation failed: ${error.message}`);
  if (!data?.questions || data.questions.length !== 3) {
    throw new Error(data?.error || 'Question generation returned an unexpected result');
  }
  return data.questions;
}

// Runs all 3 questions through the real query pipeline and flags results
// that need the admin's attention:
//   - simple/moderate: flagged if the answer's citations don't include this
//     document (should have answered from itself but didn't)
//   - adversarial: flagged only if an answer that cites this document states
//     figures that DON'T appear in the document's text (possible hallucination),
//     or states no checkable figures at all. A grounded partial answer — e.g.
//     "dues collected in July: $0.00, per the P&L" — passes (changed 2026-09-27;
//     it used to flag every non-refusal that cited the document).
const FALLBACK_SENTENCE = "This information isn't in the documents I have access to right now.";

// Money-like figures: "$1,150.00", "1,150", "37,758.55", "97.6%". Bare small
// integers, years and dates are ignored — they're not the facts at risk.
const FIGURE_PATTERN = /-?\$?\(?\d{1,3}(?:,\d{3})+(?:\.\d+)?\)?%?|-?\$\d+(?:\.\d+)?|-?\d+\.\d{2}\b|\d+(?:\.\d+)?%/g;

function figureValue(raw) {
  const cleaned = raw.replace(/[$,()%\s]/g, '').replace(/^-/, '');
  const value = Number(cleaned);
  return Number.isFinite(value) ? value.toFixed(2) : null;
}

// Figures stated in `answerText` whose numeric value (sign-insensitive) never
// appears in `sourceText`. Returns the figures as written in the answer.
export function ungroundedFigures(answerText, sourceText) {
  const sourceValues = new Set((String(sourceText || '').match(FIGURE_PATTERN) || []).map(figureValue).filter(Boolean));
  const stated = String(answerText || '').match(FIGURE_PATTERN) || [];
  return [...new Set(stated)].filter((f) => {
    const v = figureValue(f);
    return v !== null && !sourceValues.has(v);
  });
}

export function statedFigures(answerText) {
  return String(answerText || '').match(FIGURE_PATTERN) || [];
}

export async function runStressTest(supabase, hoaId, userId, userRole, documentId, questions) {
  const results = [];

  // The document's own text, for checking adversarial answers' figures.
  let sourceText = '';
  try {
    const { data } = await supabase.from('documents').select('extracted_text').eq('id', documentId).maybeSingle();
    sourceText = data?.extracted_text || '';
  } catch {
    sourceText = '';
  }

  for (const q of questions) {
    let result;
    try {
      const answer = await runRAGQuery(supabase, hoaId, userId, userRole, q.question, [], null, { includeDocumentId: documentId });
      const citedThisDoc = (answer.citations || []).some(c => c.document_id === documentId);
      const isFallback = (answer.answer || '').includes(FALLBACK_SENTENCE);

      let flag = null;
      if (q.level === 'adversarial') {
        if (citedThisDoc && !isFallback) {
          const figures = statedFigures(answer.answer);
          const ungrounded = ungroundedFigures(answer.answer, sourceText);
          if (ungrounded.length > 0) {
            flag = `This answer states figures that don't appear in this document (${ungrounded.slice(0, 4).join(', ')}) — check for hallucination.`;
          } else if (figures.length === 0) {
            flag = 'This document answered a question it may not be able to answer — check that the answer is supported by the document.';
          }
          // Every stated figure appears in the document: a grounded (partial)
          // answer, not a hallucination — no flag.
        }
      } else {
        if (!citedThisDoc && !isFallback) {
          flag = 'The answer did not cite this document — retrieval may not be finding this document\'s content.';
        } else if (isFallback) {
          flag = 'The pipeline could not find an answer in this document, even though it should be able to.';
        }
      }

      result = {
        level: q.level,
        question: q.question,
        answer: answer.answer,
        citations: answer.citations || [],
        citedThisDoc,
        isFallback,
        flag,
        error: null,
      };
    } catch (err) {
      result = {
        level: q.level,
        question: q.question,
        answer: null,
        citations: [],
        citedThisDoc: false,
        isFallback: false,
        flag: null,
        error: err.message,
      };
    }
    results.push(result);
  }

  return results;
}
